// import { target } from "@github/catalyst";
import { azureTokenInfo } from "../utils/azureTokenInfo";
import { controller } from "./controller";
import { debounce } from "@github/mini-throttle/dist/decorators";
import { envConfig } from "../config/envConfigs";
import { environment } from "../constants/environment";
import { getDeviceType } from "../utils/deviceType";
import { loggingFetch } from "../utils/loggingFetch";

type Address = {
  addressWithoutZip4: string;
  addressGuid?: string;
  origins?: Origin[];
};

type Origin = {
  address: string;
  addressGuid: string;
  origin: string;
  originKey: string;
  sourceAddress: string;
  sourceLatLong: any;
};

type Serviceability = {
  locationId?: string;
  addressMatch?: string;
  serviceabilityColor: "red" | "yellow" | "green";
  serviceabilityDescription?: string;
  serviceability?: string[];
  capabilities?: [];
  title?: string;
  description?: string;
  estimatedRelease?: string | null;
  estimatedReleaseDate?: string | null;
  partner?: string | null;
  addressSource?: string;
  elocId?: string;
  success?: string;
  errorCode?: string;
  errorMessage?: string;
};

const fetchAddresses = async (address: string): Promise<any> => {
  const response = await loggingFetch(`${envConfig.urls.typeahead}?streetAddress=${address}`, {
    headers: {
      AuthType: "AzureAD",
      Authorization: `Bearer ${(await azureTokenInfo).access_token}`,
      deviceType: getDeviceType(),
      environment,
      sessionId: `${sessionStorage.getItem("ev_sessionId")}`,
    },
  });
  if (response.ok) {
    return (await response.json()) as Address[];
  } else {
    return Promise.resolve([]);
  }
};

const checkServiceability = async (addressGuid: string): Promise<any> => {
  const requestBody = {
    feProcessId: envConfig?.appConfig?.serviceabilityFlowId,
    ...envConfig?.appConfig?.appDetails,
    addressGuid: addressGuid,
    environment: environment,
    deviceType: getDeviceType(),
  };
  const response = await loggingFetch(`${envConfig.urls.serviceability}`, {
    headers: {
      AuthType: "AzureAD",
      Authorization: `Bearer ${(await azureTokenInfo).access_token}`,
      "Content-Type": "application/json",
    },
    method: "POST",
    body: JSON.stringify(requestBody),
  });
  if (response.ok) {
    return (await response.json()) as Serviceability;
  } else {
    return Promise.resolve({});
  }
};

const buildOptions = (addresses: Address[]): HTMLLIElement[] =>
  addresses.map((address) => {
    // Non serviceable address - append "-028" to the originKey
    let addressGuidTemp = address.addressGuid;
    let housekey = "";
    if (address.origins) {
      address.origins.filter((data) => {
        if (data.origin === "csg") {
          housekey = data.originKey;
        }
      });
    }
    const li: HTMLLIElement = document.createElement("li");
    li.setAttribute("id", `${address.addressGuid}`);
    li.setAttribute("locationid", `${addressGuidTemp}`);
    li.setAttribute("housekey", `${housekey}`);
    li.appendChild(document.createTextNode(address.addressWithoutZip4));
    return li;
  });

@controller
export default class AddressTypeaheadElement extends HTMLElement {
  //@target
  input: HTMLInputElement | undefined | null;
  //@target
  datalist: HTMLUListElement | undefined | null;
  clearicon: HTMLImageElement | undefined | null;
  selectedServicabilitylabel: HTMLLabelElement | undefined | null;
  isSearchAddressCallTriggered: any = false;
  isServiceabilityCallTriggered: any = false;
  serviceabilityMessage: HTMLLabelElement | undefined | null;
  loadingEl: any;
  selectedServiceabilityMessage: any;
  successResponse: any;
  submitButton: any;
  notifyMeButton: HTMLButtonElement | undefined | null;

  connectedCallback(): void {
    // eslint-disable-next-line custom-elements/no-dom-traversal-in-connectedcallback
    this.input = this.querySelector("input");
    // eslint-disable-next-line custom-elements/no-dom-traversal-in-connectedcallback
    this.datalist = this.querySelector("ul");
    // eslint-disable-next-line custom-elements/no-dom-traversal-in-connectedcallback
    this.clearicon = this.querySelector("img");
    // eslint-disable-next-line custom-elements/no-dom-traversal-in-connectedcallback
    this.serviceabilityMessage = this.querySelector("#serviceabilityMessage") as HTMLLabelElement;
    // eslint-disable-next-line custom-elements/no-dom-traversal-in-connectedcallback
    this.loadingEl = document.getElementById("inlineLoader");
    this.selectedServiceabilityMessage = document.getElementById("selectedServiceabilityMessage") as HTMLInputElement;
    this.successResponse = document.getElementById("successResponse") as HTMLInputElement;
    let value = this.selectedServiceabilityMessage?.value;
    let successResponseValue = this.successResponse?.value;
    this.submitButton = document.querySelector(".retail-av-service-address-screen-btn");
    // Get the Notify Me When Available button and hide it initially
    this.notifyMeButton = document.querySelector('[rsCustomValidator="navigateToXoe"]') as HTMLButtonElement;
    if (this.notifyMeButton) {
      this.notifyMeButton.style.display = "none";
    }

    if (this.input && this.datalist && this.clearicon && this.serviceabilityMessage) {
      this.input.addEventListener("input", this.handleInput.bind(this));
      this.input.addEventListener("focus", this.buildListItems.bind(this));
      this.clearicon.addEventListener("click", this.resetForm.bind(this));
      this.input?.setAttribute("class", "default-border");
      this.clearicon?.setAttribute("class", "hide");
      this.serviceabilityMessage?.setAttribute("class", "hide");
      this.datalist?.setAttribute("class", "hide");
    }
    if (successResponseValue !== "false" || value === "error") {
      if (value) {
        this.clearicon?.classList.remove("hide");
        this.clearicon?.setAttribute("class", "clear-icon");
        this.createServiceabilityMessage(value, this.input?.value);
      }
    } else {
      this.clearicon?.classList.remove("hide");
      this.clearicon?.setAttribute("class", "clear-icon");
      this.createServiceabilityMessage(value, this.input?.value, successResponseValue);
    }
  }

  @debounce(500)
  async handleInput(): Promise<void> {
    if (!this.input?.value) {
      this.removeAllChildren(this.datalist);
      this.resetForm();
    } else {
      if (
        this.selectedServicabilitylabel?.getAttribute("class") !== "hide" &&
        this.selectedServicabilitylabel?.getAttribute("class") !== undefined &&
        this.selectedServicabilitylabel?.getAttribute("class") !== null
      ) {
        this.resetFormOnEdit();
      }
      this.clearicon?.classList.remove("hide");
      this.clearicon?.setAttribute("class", "clear-icon");
      await this.buildListItems();
    }
  }

  async handleListItemClick(event?: any): Promise<void> {
    //Fetching AddressGuid from Selected List item
    const addressGUID: string = event.target?.id.toString();

    //Setting AddressGuid as typeahead custom control value
    const typeaheadControl: any = document.querySelector("#typeahead");
    if (typeaheadControl) {
      typeaheadControl.value = addressGUID;
      typeaheadControl.setAttribute("value", addressGUID);
    }

    //selectedAddressValue - hidden field in the UI - Set location id (AddressGuid + 025/028) value. This
    //input for book appointment
    const selectedAddressValue: any = document.querySelector("#selectedAddressValue");
    if (event.target?.getAttribute("locationid")?.toString() && selectedAddressValue) {
      selectedAddressValue.value = event.target?.getAttribute("locationid")?.toString();
      selectedAddressValue.setAttribute("value", event.target?.getAttribute("locationid")?.toString());
    }

    //selectedHouseKey - hidden field in ui - set housekey for EBI flow input
    const selectedHouseKey: any = document.querySelector("#selectedHouseKey");
    if (event.target?.getAttribute("housekey")?.toString() && selectedHouseKey) {
      selectedHouseKey.value = event.target?.getAttribute("housekey")?.toString();
      selectedHouseKey.setAttribute("value", event.target?.getAttribute("housekey")?.toString());
    }

    //TODO: Can be removed. added for databinding
    const address: string = event.target?.textContent.toString();
    this.input?.setAttribute("selectedaddress", address);

    if (!!this.input && !!address && !!addressGUID && !this.isSearchAddressCallTriggered) {
      this.input.value = address;
      this.hideElement(this.datalist);
      this.loadingEl.style.display = "inline-block";
      this.isServiceabilityCallTriggered = true;
      this.selectedServicabilitylabel = null;
      const isServiceable = await checkServiceability(addressGUID);
      this.loadingEl.style.display = "none";
      if (isServiceable) {
        // Store elocId from serviceability response for navigateToXoe
        if (isServiceable.elocId) {
          const selectedAddressEl: any = document.querySelector("#selectedAddressValue");
          if (selectedAddressEl) {
            selectedAddressEl.value = isServiceable.elocId;
            selectedAddressEl.setAttribute("value", isServiceable.elocId);
          }
        }
        this.createServiceabilityMessage(
          isServiceable?.serviceabilityColor,
          address,
          isServiceable?.success,
          isServiceable?.estimatedRelease,
          isServiceable?.title,
          isServiceable?.description
        );
      }
    }
    this.hideElement(this.datalist);
  }

  async buildListItems(): Promise<void> {
    // this.serviceabilityMessage?.setAttribute("class", "hide");
    this.input?.setAttribute("class", "default-border");

    if (this.input?.value && this.datalist && !this.isServiceabilityCallTriggered) {
      this.isSearchAddressCallTriggered = true;
      this.loadingEl.style.display = "inline-block";
      //Fetch address API call
      this.submitButton.classList.remove("enableButton");
      this.submitButton.classList.add("disabledButton");
      const address = await fetchAddresses(this.input.value);
      this.loadingEl.style.display = "none";
      this.isSearchAddressCallTriggered = false;
      this.removeAllChildren(this.datalist);
      if (Array.isArray(address) && address.length === 0 && !this.isServiceabilityCallTriggered) {
        this.submitButton.classList.add("enableButton");
        this.submitButton.classList.remove("disabledButton");
        this.selectedServiceabilityMessage?.setAttribute("value", "error");
        this.showInvalidAddressMessage();
        this.hideElement(this.datalist);
      } else {
        if (!this.isServiceabilityCallTriggered) {
          this.serviceabilityMessage?.setAttribute("class", "hide");
          this.input?.setAttribute("class", "default-border");
          for (const li of buildOptions(address._embedded.addresses)) {
            li.addEventListener("click", this.handleListItemClick.bind(this));
            this.datalist?.appendChild(li);
          }
          this.datalist?.setAttribute("class", "dropdown-menu");
        }
      }
    }
  }

  hideElement(element: HTMLUListElement | undefined | null): void {
    if (element) {
      element.setAttribute("class", "hide");
      while (element.firstChild) {
        element.removeChild(element.firstChild);
      }
    }
  }

  removeAllChildren(parent: HTMLUListElement | undefined | null): void {
    while (parent?.firstChild) {
      parent.removeChild(parent.firstChild);
    }
  }

  showInvalidAddressMessage(success?: any): void {
    if (!!this.serviceabilityMessage && !!this.input) {
      // this.errorlabel.textContent = "Service not found, please retry with other address";
      if (success === "false") {
        this.serviceabilityMessage.textContent = "It looks like we run into error, please try again.";
        this.serviceabilityMessage?.setAttribute("class", "retail-av-serviceablity-error-message");
      } else {
        this.serviceabilityMessage.textContent =
          "The address you entered could not be recognized. Would you still like to book an appointment at an Xfinity store?";
        this.serviceabilityMessage?.setAttribute("class", "retail-av-serviceablity-error-message");
      }

      this.input?.setAttribute("class", "error-border");
    }
  }

  resetForm(): void {
    if (this.input) {
      this.input.value = "";
    }
    this.hideElement(this.datalist);
    this.clearicon?.setAttribute("class", "hide");
    this.input?.setAttribute("class", "default-border");
    this.serviceabilityMessage?.setAttribute("class", "hide");
    this.isSearchAddressCallTriggered = false;
    this.isServiceabilityCallTriggered = false;
    this.loadingEl.style.display = "none";
    this.submitButton.classList.remove("enableButton");
    this.submitButton.classList.add("disabledButton");
    this.selectedServiceabilityMessage?.setAttribute("value", "");
    this.successResponse?.setAttribute("value", "");
    const selectedAddressValue: any = document.querySelector("#selectedAddressValue");
    const selectedHouseKey: any = document.querySelector("#selectedHouseKey");
    if (selectedHouseKey) {
      selectedHouseKey.value = "";
      selectedHouseKey.setAttribute("value", "");
    }
    if (selectedAddressValue) {
      selectedAddressValue.value = "";
      selectedAddressValue.setAttribute("value", "");
    }
    // Hide Notify Me button on reset
    if (this.notifyMeButton) {
      this.notifyMeButton.style.display = "none";
    }
  }

  resetFormOnEdit(): void {
    this.input?.setAttribute("class", "default-border");
    this.serviceabilityMessage?.setAttribute("class", "hide");
    this.isServiceabilityCallTriggered = false;
    this.loadingEl.style.display = "none";
    const selectedAddressValue: any = document.querySelector("#selectedAddressValue");
    if (selectedAddressValue) {
      selectedAddressValue.value = "";
      selectedAddressValue.setAttribute("value", "");
    }
    const selectedHouseKey: any = document.querySelector("#selectedHouseKey");
    if (selectedHouseKey) {
      selectedHouseKey.value = "";
      selectedHouseKey.setAttribute("value", "");
    }
    // Hide Notify Me button on edit
    if (this.notifyMeButton) {
      this.notifyMeButton.style.display = "none";
    }
  }

  createServiceabilityMessage(
    serviceabilityColor: any,
    address: any,
    success?: any,
    estimatedRelease?: string | null,
    title?: string,
    description?: string
  ) {
    this.submitButton.classList.remove("disabledButton");
    this.submitButton.classList.add("enableButton");
    this.selectedServiceabilityMessage?.setAttribute("value", serviceabilityColor);
    this.successResponse?.setAttribute("value", success);
    // Hide Notify Me button by default, will show only when estimatedRelease is not null
    if (this.notifyMeButton) {
      this.notifyMeButton.style.display = "none";
    }

    // Show Notify Me button and display title/description banner when estimatedRelease is available
    // and enablePendingConstruction app setting is true
    const isPendingConstructionEnabled = envConfig?.appConfig?.enablePendingConstruction;

    if (isPendingConstructionEnabled && estimatedRelease !== null && estimatedRelease !== "" && this.notifyMeButton) {
      this.notifyMeButton.style.display = "inline-block";

      // Display banner with title and description from response
      if (title || description) {
        const titleHtml = title ? `<strong>${title}</strong><br>` : "";
        // Replace {contactinformation} with a clickable link
        const processedDescription = description
          ? description.replace(
              /\{contactinformation\}/gi,
              `<a href="javascript:void(0)" id="contactInfoLink" class="retail-av-contact-info-link">contact information</a>`
            )
          : "";
        const bannerContent = titleHtml + processedDescription;
        const html: Node = document.createRange().createContextualFragment(bannerContent);

        this.serviceabilityMessage?.setAttribute("class", "retail-av-sub-heading");
        if (!this.serviceabilityMessage?.hasChildNodes()) {
          this.serviceabilityMessage?.appendChild(html);
        } else {
          while (this.serviceabilityMessage?.firstChild) {
            this.serviceabilityMessage?.removeChild(this.serviceabilityMessage?.firstChild);
          }
          this.serviceabilityMessage?.appendChild(html);
        }

        // Add click event listener to the contact information link
        const contactInfoLink = document.getElementById("contactInfoLink");
        if (contactInfoLink) {
          contactInfoLink.addEventListener("click", () => {
            // Trigger the same action as Notify Me When Available button
            this.notifyMeButton?.click();
          });
        }

        this.selectedServicabilitylabel = this.serviceabilityMessage;
        return;
      }
    }

    if (serviceabilityColor === "red") {
      const line1 = `Unfortunately, Xfinity service is not available at this address. We are unable to locate deals for `;
      const line2 = `. Would you still like to book an appointment at an Xfinity store?`;
      const outOfFootprint = line1 + address + line2;
      const html: Node = document.createRange().createContextualFragment(outOfFootprint);

      this.serviceabilityMessage?.setAttribute("class", "retail-av-sub-heading");
      if (!this.serviceabilityMessage?.hasChildNodes()) {
        this.serviceabilityMessage?.appendChild(html);
      } else {
        while (this.serviceabilityMessage?.firstChild) {
          this.serviceabilityMessage?.removeChild(this.serviceabilityMessage?.firstChild);
        }
        this.serviceabilityMessage?.appendChild(html);
      }
      this.selectedServicabilitylabel = this.serviceabilityMessage;
    } else if (serviceabilityColor === "green") {
      const line1 = `Great news: Xfinity Services are available at this address!`;
      const serviceable = line1;
      const html: Node = document.createRange().createContextualFragment(serviceable);
      this.serviceabilityMessage?.setAttribute("class", "retail-av-sub-heading");
      if (!this.serviceabilityMessage?.hasChildNodes()) {
        this.serviceabilityMessage?.appendChild(html);
      } else {
        while (this.serviceabilityMessage?.firstChild) {
          this.serviceabilityMessage?.removeChild(this.serviceabilityMessage?.firstChild);
        }
        this.serviceabilityMessage?.appendChild(html);
      }
      this.selectedServicabilitylabel = this.serviceabilityMessage;
    } else if (serviceabilityColor === "yellow") {
      const line1 = `Unfortunately, Xfinity service is not currently available at this address. Please go `;
      // eslint-disable-next-line github/unescaped-html-literal
      const link = `<a href="https://www.xfinity.com/support/articles/determining-serviceability" target="_blank">here</a
      >`;
      const line2 = ` to determine what steps are involved to get you set up. Would you still like to book an appointment at
      an Xfinity store?`;
      const proximityMessage = line1 + link + line2;
      const html: Node = document.createRange().createContextualFragment(proximityMessage);
      this.serviceabilityMessage?.setAttribute("class", "retail-av-sub-heading");
      if (!this.serviceabilityMessage?.hasChildNodes()) {
        this.serviceabilityMessage?.appendChild(html);
      } else {
        while (this.serviceabilityMessage?.firstChild) {
          this.serviceabilityMessage?.removeChild(this.serviceabilityMessage?.firstChild);
        }
        this.serviceabilityMessage?.appendChild(html);
      }
      this.selectedServicabilitylabel = this.serviceabilityMessage;
    } else if (success === "false") {
      this.serviceabilityMessage?.setAttribute("class", "hide");
      this.showInvalidAddressMessage(success);
    } else {
      serviceabilityColor = "error";
      this.serviceabilityMessage?.setAttribute("class", "hide");
      this.showInvalidAddressMessage(success);
    }
  }
}

declare global {
  interface Window {
    AddressTypeaheadElement: typeof AddressTypeaheadElement;
  }
  interface HTMLElementTagNameMap {
    "address-typeahead": AddressTypeaheadElement;
  }
}

if (!window.customElements.get("address-typeahead")) {
  window.AddressTypeaheadElement = AddressTypeaheadElement;
  window.customElements.define("address-typeahead", AddressTypeaheadElement);
}
